import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  User,
  Calendar,
  Eye,
  RefreshCw,
  Wallet,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Layers,
  Trash2,
  Plus,
  Minus,
  Download,
  FileText,
  MessageSquare,
  Save,
  ChevronLeft,
  ChevronRight,
  Search,
  ChevronDown,
  RefreshCwIcon,
  Copy,
  List,
  LayoutGrid,
  User2,
} from "lucide-react";
import generateMonthDates from "../../components/Common/Date";
import jsPDF from "jspdf";
import { formatCurrencyINR } from "../../utils/function";
import { useTranslation } from "react-i18next";
import { getCustomerProductFavList } from "../../services/customerServices";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import {
  createCustomerLedger,
  exportCustomerLedgerCSV,
  exportCustomerLedgerPDF,
  getAllCustomerSerachStoreOwner,
  getAllCustomerSerachStoreWiseList,
  getCustomerLedgerDetails,
} from "../../services/storeOwnerOrderServices";
import { notify } from "../../components/Common/Alerts";
import CONFIG from "../../config";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

const ChartProduct = () => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentDate = new Date();

  const monthOptions = Array.from({ length: 12 }, (_, i) =>
    new Date(0, i).toLocaleString("default", {
      month: "long",
    }),
  );

  const [selectedMonth, setSelectedMonth] = useState(
    currentDate.toLocaleString("default", {
      month: "long",
    }),
  );

  const [selectedYear, setSelectedYear] = useState(
    currentDate.getFullYear().toString(),
  );
  const [data, setData] = useState([]);
  const [activeDateIndex, setActiveDateIndex] = useState(0);
  const [saveStatus, setSaveStatus] = useState("saved");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [customerList, setCustomerList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [filters, setFilters] = useState({
    selectedMonth: "May",
    selectedYear: "2026",
    selectedCustomer: "",
  });
  const scrollContainerRef = useRef(null);
  const getYesterdayIndex = () => activeDateIndex - 1;
  const saveTimer = useRef(null);
  const isDirty = useRef(false);
  const lastSavedRef = useRef(null);
  const storedData = JSON.parse(localStorage.getItem(CONFIG.login_data));
  const savingRef = useRef(false);
  const timeoutRef = useRef(null);
  const isHydrating = useRef(true);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    setViewMode(isMobile ? "grid" : "list");
  }, []);

  const markDirty = (updater) => {
    isDirty.current = true;
    setData(updater);
  };

  useEffect(() => {
    const data = {
      search: "",
      page: "",
      limit: "",
    };

    getAllCustomerSerachStoreWiseList(data)
      .then((response) => {
        const decrypted = decryptData(response.data.data);

        setCustomerList(decrypted.data || []);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  const customerOptions = customerList.map((customer) => ({
    value: customer.id,
    label: customer.name,
    mobile: customer.mobile,
    image: customer.profile_image,
  }));

  useEffect(() => {
    if (selectedCustomer) {
      const data = {
        store_id: storedData?.active_store_id,
        category_ids: [],
        search: "",
        page: "",
        limit: 10,
        customer_id: selectedCustomer?.value,
      };

      getCustomerProductFavList(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);
          setProductList(decrypted.data);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [selectedCustomer]);

  const generateInitialData = () => {
    const generated = generateMonthDates(selectedMonth, selectedYear);

    const formattedProducts = productList.flatMap((product) =>
      product.units.map((unit) => ({
        id: `${product.id}-${unit.variant_id}`,
        product_id: Number(product.id),
        unit_id: Number(unit.variant_id),
        name: `${product.name} ${unit.unit_qty}${unit.short_name}`,
        unit: unit.unit_name,
        short_name: unit.short_name,
        selling: Number(unit.selling_price),
        cost_price: Number(unit.cost_price || 0),
        qty: 0,
        image: "https://placehold.co/40x40",
        default_unit: unit.default_unit,
        unit_qty: unit.unit_qty,
      })),
    );

    return generated.map((day) => {
      const dateObj = new Date(day.date);

      return {
        date: day.date,

        dayNum: dateObj.getDate(),

        dayName: dateObj.toLocaleDateString("en-US", {
          weekday: "short",
        }),

        products: formattedProducts.map((item) => ({
          ...item,
        })),

        summary: {
          paid: 0,
        },
      };
    });
  };

  const fetchLedger = async () => {
    try {
      isFetchingRef.current = true;

      const res = await getCustomerLedgerDetails({
        customer_id: Number(selectedCustomer.value),
        month: selectedMonth,
        year: selectedYear,
      });

      const decrypted = decryptData(res.data.data || []);
      const ledger = decrypted.data || [];

      const initialData = generateInitialData();

      const merged = initialData?.map((day) => {
        const existing = ledger.find((d) => d.date === day.date);

        return {
          ...day,

          summary: {
            paid: Number(existing?.summary?.paid || 0),
            temp_paid: 0,
          },

          products: day.products.map((p) => {
            const saved = existing?.products?.find(
              (sp) =>
                String(sp.product_id) === String(p.product_id) &&
                Number(sp.unit_id) === Number(p.unit_id),
            );

            return {
              ...p,
              qty: Number(saved?.qty || 0),
              selling: Number(saved?.selling ?? p.selling),
              cost_price: Number(saved?.cost_price ?? p.cost_price),
            };
          }),
        };
      });

      setData(merged);
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setTimeout(() => {
        isFetchingRef.current = false;
      }, 300);
    }
  };

  useEffect(() => {
    if (!selectedCustomer?.value || !productList.length) {
      setData(generateInitialData());
      return;
    }

    fetchLedger();
  }, [selectedCustomer, selectedMonth, selectedYear, productList]);

  useEffect(() => {
    if (!data.length || !selectedCustomer) return;

    if (isFetchingRef.current) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    setSaveStatus("saving");

    timeoutRef.current = setTimeout(async () => {
      const currentDay = data[activeDateIndex];
      if (!currentDay) return;

      const filteredProducts = currentDay.products
        .filter((p) => Number(p.qty) > 0)
        .map((p) => ({
          product_id: p.product_id,
          unit_id: p.unit_id || null,
          product_name: p.name,
          unit_name: p.unit,
          unit_short_name: p.short_name,
          unit_qty: p.unit_qty || 1,
          qty: Number(p.qty),
          selling_price: Number(p.selling),
          cost_price: Number(p.cost_price || 0),
        }));

      const payload = {
        customer_id: selectedCustomer.value,
        ledger_date: currentDay.date,
        paid_amount: Number(currentDay.summary?.paid || 0),
        note: "",
        products: filteredProducts,
      };

      if (savingRef.current) return;
      savingRef.current = true;

      try {
        if (!filteredProducts.length) {
          setSaveStatus("saved");
          savingRef.current = false;
          return;
        }

        const response = await createCustomerLedger(payload);
        const decrypted = decryptData(response.data.data);

        setSaveStatus("saved");
        await fetchLedger();
      } catch (err) {
        notify.error(Utils.getErrorMessage(err));
        setSaveStatus("error");
      } finally {
        savingRef.current = false;
      }
    }, 1200);

    return () => clearTimeout(timeoutRef.current);
  }, [data, activeDateIndex, selectedCustomer]);

  const handleQtyChange = (productId, modifier) => {
    markDirty((prevData) =>
      prevData.map((day, idx) => {
        if (idx !== activeDateIndex) return day;

        const updatedProducts = day.products.map((p) => {
          if (p.id !== productId) return p;

          return {
            ...p,
            qty: Math.max(0, p.qty + modifier),
          };
        });

        return {
          ...day,
          products: updatedProducts,
        };
      }),
    );
  };

  const handleManualQtyChange = (productId, value) => {
    markDirty((prevData) =>
      prevData.map((day, idx) => {
        if (idx !== activeDateIndex) return day;

        const updatedProducts = day.products.map((p) => {
          if (p.id !== productId) return p;

          return {
            ...p,
            qty: Math.max(0, Number(value) || 0),
          };
        });

        return {
          ...day,
          products: updatedProducts,
        };
      }),
    );
  };

  const handlePriceChange = (productId, field, value) => {
    markDirty((prevData) =>
      prevData.map((day, idx) => {
        if (idx !== activeDateIndex) return day;

        const updatedProducts = day.products.map((p) => {
          if (p.id !== productId) return p;

          return {
            ...p,
            [field]: Number(value) || 0,
          };
        });

        return {
          ...day,
          products: updatedProducts,
        };
      }),
    );
  };

  const currentDayData = data[activeDateIndex] || {
    products: [],
    summary: {
      paid: 0,
    },
  };

  const filteredProducts = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    if (!query) return currentDayData.products;

    return currentDayData.products.filter((product) =>
      product.name.toLowerCase().includes(query),
    );
  }, [searchQuery, currentDayData.products]);

  const computedTotals = useMemo(() => {
    const sumAmount = currentDayData.products.reduce(
      (acc, product) =>
        acc + Number(product.qty || 0) * Number(product.selling || 0),
      0,
    );

    const sumQty = currentDayData.products.reduce(
      (acc, product) => acc + Number(product.qty || 0),
      0,
    );

    const paid = Number(currentDayData.summary?.paid || 0);
    const pending = Math.max(sumAmount - paid, 0);
    const isFullyPaid = sumAmount > 0 && pending <= 0;
    return {
      uniqueCount: currentDayData.products.filter((p) => Number(p.qty || 0) > 0)
        .length,
      sumQty,
      sumAmount,
      paid,
      pending,
      isFullyPaid,
    };
  }, [currentDayData]);

  const handleScroll = (direction) => {
    if (!scrollContainerRef.current) return;
    const scrollAmount = 250;
    scrollContainerRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const monthSummary = useMemo(() => {
    let totalAmount = 0;
    let totalPaid = 0;
    let totalEntries = 0;
    let totalQty = 0;
    let totalCost = 0;

    data.forEach((day) => {
      const dayTotal = day.products.reduce(
        (acc, p) => acc + Number(p.qty || 0) * Number(p.selling || 0),
        0,
      );

      const dayCost = day.products.reduce(
        (acc, p) =>
          acc + Number(p.qty || 0) * Number(p.cost_price || p.whole || 0),
        0,
      );

      const paid = Number(day.summary?.paid || 0);

      totalAmount += dayTotal;
      totalPaid += paid;
      totalEntries += day.products.length;
      totalQty += day.products.reduce((acc, p) => acc + Number(p.qty || 0), 0);
      totalCost += dayCost;
    });

    const pending = Math.max(totalAmount - totalPaid, 0);
    const profit = totalAmount - totalCost;

    return {
      totalAmount,
      totalPaid,
      pending,
      totalEntries,
      totalQty,
      totalCost,
      profit,
    };
  }, [data]);

  const cards = [
    {
      label: "Total Amount",
      value: formatCurrencyINR(monthSummary.totalAmount.toFixed(2)),
      icon: Wallet,
      style: "border-amber-500/30 bg-amber-500/5 text-amber-600",
    },
    {
      label: "Total Paid",
      value: formatCurrencyINR(monthSummary.totalPaid.toFixed(2)),
      icon: CheckCircle2,
      style: "border-emerald-500/30 bg-emerald-500/5 text-emerald-600",
    },
    {
      label: "Pending Amount",
      value: formatCurrencyINR(monthSummary.pending.toFixed(2)),
      icon: AlertCircle,
      style: "border-rose-500/30 bg-rose-500/5 text-rose-600",
    },
    {
      label: "Total Profit",
      value: formatCurrencyINR(monthSummary.profit.toFixed(2)),
      icon: BarChart3,
      style: "border-purple-500/30 bg-purple-500/5 text-purple-600",
    },
  ];

  const customerSummary = useMemo(() => {
    let totalAmount = 0;
    let totalPaid = 0;
    let totalCost = 0;

    data.forEach((day) => {
      const dayTotal = day.products.reduce(
        (acc, p) => acc + Number(p.qty || 0) * Number(p.selling || 0),
        0,
      );

      const dayCost = day.products.reduce(
        (acc, p) => acc + Number(p.qty || 0) * Number(p.cost_price || 0),
        0,
      );

      const paid = Number(day.summary?.paid || 0);

      totalAmount += dayTotal;
      totalPaid += paid;
      totalCost += dayCost;
    });

    const pending = Math.max(totalAmount - totalPaid, 0);
    const profit = totalAmount - totalCost;

    return {
      totalAmount,
      totalPaid,
      pending,
      profit,
    };
  }, [data]);

  // const downloadCSV = () => {
  //   const rows = [];

  //   data.forEach((day) => {
  //     day.products.forEach((p) => {
  //       rows.push({
  //         date: day.date,
  //         product: p.name,
  //         qty: p.qty,
  //         selling: p.selling,
  //         whole: p.whole,
  //         amount: p.qty * p.selling,
  //         profit: (p.selling - p.whole) * p.qty,
  //         paid: day.summary?.paid || 0,
  //       });
  //     });
  //   });

  //   const headers = Object.keys(rows[0] || {}).join(",");

  //   const csv = [headers, ...rows.map((r) => Object.values(r).join(","))].join(
  //     "\n",
  //   );

  //   const blob = new Blob([csv], { type: "text/csv" });
  //   const url = URL.createObjectURL(blob);

  //   const a = document.createElement("a");
  //   a.href = url;
  //   a.download = "ledger.csv";
  //   a.click();
  // };

  const handleDownloadCSV = async () => {
    if (!selectedCustomer.value) {
      notify.error(t("pleaseSelectCustomer"));
      return;
    }

    try {
      const payload = {
        customer_id: selectedCustomer.value,
        month: selectedMonth,
        year: selectedYear,
      };

      const res = await exportCustomerLedgerCSV(payload);

      const { link, filename } = res.data;

      const downloadLink = document.createElement("a");
      downloadLink.href = link;
      downloadLink.download = filename;

      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    }
  };

  const downloadPDF = async () => {
    const payload = {
      customer_id: selectedCustomer.value,
      month: selectedMonth,
      year: selectedYear,
    };

    const res = await exportCustomerLedgerPDF(payload);

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", "ledger.pdf");

    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // const downloadPDF = () => {
  //   const doc = new jsPDF();

  //   const rows = [];

  //   data.forEach((day) => {
  //     day.products.forEach((p) => {
  //       rows.push([
  //         day.date,
  //         p.name,
  //         p.qty,
  //         p.selling,
  //         p.qty * p.selling,
  //         (p.selling - p.whole) * p.qty,
  //       ]);
  //     });
  //   });

  //   autoTable(doc, {
  //     head: [["Date", "Product", "Qty", "Sell", "Amount", "Profit"]],
  //     body: rows,
  //   });

  //   doc.save("ledger.pdf");
  // };

  const sendWhatsApp = () => {
    const total = monthSummary.totalAmount.toFixed(2);
    const paid = monthSummary.totalPaid.toFixed(2);
    const pending = monthSummary.pending.toFixed(2);

    const message = `🧾 Customer Ledger
        💰 Total: ${formatCurrencyINR(total)}
        ✅ Paid: ${formatCurrencyINR(paid)}
        ❗Pending: ${formatCurrencyINR(pending)}
        📊 Profit: ${formatCurrencyINR(monthSummary.profit)}`;

    const phone = "91XXXXXXXXXX";
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const cloneProducts = (products, resetQty = true) => {
    return products.map((p) => ({
      ...p,
      qty: resetQty ? 0 : p.qty,
    }));
  };

  const copyYesterday = () => {
    markDirty((prev) => {
      const current = activeDateIndex;
      if (current === 0) return prev;

      const yesterday = prev[current - 1];

      return prev.map((d, idx) => {
        if (idx !== current) return d;

        return {
          ...d,
          products: cloneProducts(yesterday.products, true),
          summary: { paid: 0 },
        };
      });
    });
  };

  const copyFrequentItems = () => {
    markDirty((prev) => {
      const current = activeDateIndex;

      const freqMap = {};

      prev.forEach((day) => {
        day.products.forEach((p) => {
          freqMap[p.id] = freqMap[p.id] || { ...p, count: 0 };
          freqMap[p.id].count += p.qty;
        });
      });

      const frequent = Object.values(freqMap)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
        .map(({ count, ...rest }) => ({
          ...rest,
          qty: 0,
        }));

      return prev.map((d, idx) => {
        if (idx !== current) return d;

        return {
          ...d,
          products: frequent,
          summary: { paid: 0 },
        };
      });
    });
  };

  const isFullyPaid = computedTotals.isFullyPaid;

  return (
    <div className="w-full bg-[#FFFBF7] dark:bg-[#0a192f] p-6 text-slate-700 dark:text-slate-200 font-sans antialiased rounded-2xl border border-slate-200 dark:border-slate-800/40 shadow-xl transition-colors duration-200">
      <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4 items-end bg-white dark:bg-[#0c1a30] border border-slate-200 dark:border-slate-800/80 p-4 rounded-xl shadow-sm">
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-2">
            {t("selectCustomer")}
          </label>

          <div className="relative">
            <User className="absolute left-3 top-3 w-4 h-4 text-slate-400 dark:text-slate-500" />

            <Select
              options={customerOptions}
              placeholder="Select Customer"
              onChange={(selected) => {
                setSelectedCustomer(selected || "");
                setFilters((prev) => ({
                  ...prev,
                  selectedCustomer: selected,
                }));
              }}
              className="text-xs"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              formatOptionLabel={(data) => (
                <div className="flex items-center gap-2 py-1">
                  {data.image ? (
                    <img
                      src={data.profile_image}
                      alt={data.label}
                      className="w-8 h-8 rounded-full object-cover border"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}

                  <div
                    className={`w-8 h-8 rounded-full bg-slate-100 border items-center justify-center ${
                      data.image ? "hidden" : "flex"
                    }`}
                  >
                    <User2 className="w-4 h-4 text-slate-500" />
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      {data.label}
                    </p>

                    <p className="text-xs text-slate-500">{data.mobile}</p>
                  </div>
                </div>
              )}
              isClearable
            />
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-2">
            {t("selectMonth")}
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-3 w-4 h-4 text-slate-400 dark:text-slate-500" />
            <select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                setFilters((prev) => ({
                  ...prev,
                  selectedMonth: e.target.value,
                }));
              }}
              className="w-full bg-slate-50 dark:bg-[#112240] border border-slate-300 dark:border-slate-700/60 rounded-lg pl-10 pr-4 py-2 text-xs text-slate-800 dark:text-white focus:outline-none appearance-none cursor-pointer"
            >
              {months?.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-2">
            {t("selectYear")}
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-3 w-4 h-4 text-slate-400 dark:text-slate-500" />
            <select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(Number(e.target.value));
                setFilters((prev) => ({
                  ...prev,
                  selectedYear: e.target.value,
                }));
              }}
              className="w-full bg-slate-50 dark:bg-[#112240] border border-slate-300 dark:border-slate-700/60 rounded-lg pl-10 pr-4 py-2 text-xs text-slate-800 dark:text-white focus:outline-none appearance-none cursor-pointer"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          onClick={() =>
            navigate("/customer-report", {
              state: { filters },
            })
          }
          className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs px-4 py-2.5 rounded-lg transition duration-150 shadow-md"
        >
          <Eye className="w-4 h-4" /> {t("viewLedger")}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {cards?.map((card, idx) => (
          <div
            key={idx}
            className={`border ${card.style.split(" ")[0]} p-4 rounded-xl flex items-center gap-4 bg-white dark:bg-[#0c1a30]/50 shadow-sm`}
          >
            <div
              className={`p-2 rounded-lg ${card.style.split(" ")[1]} ${card.style.split(" ")[2]}`}
            >
              <card.icon className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium tracking-wide">
                {card.label}
              </p>
              <p className="text-sm font-bold mt-0.5 text-slate-900 dark:text-white">
                {card.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white dark:bg-[#0c1a30]/50 border border-slate-200 dark:border-slate-800/80 p-4 rounded-xl shadow-sm">
            <h2 className="text-[11px] font-bold uppercase tracking-wider text-orange-500 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> {t("selectEntryDate")}
            </h2>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleScroll("left")}
                className="p-2 rounded-full bg-[#0a192f] hover:bg-orange-500 text-white transition-colors flex items-center justify-center min-w-[34px] h-[34px]"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div
                ref={scrollContainerRef}
                className="flex gap-2.5 overflow-x-auto py-1 scroll-smooth snap-x w-full"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {data?.map((day, index) => {
                  const isActive = activeDateIndex === index;
                  const hasEntries = day.products?.some(
                    (product) => product.qty > 0,
                  );

                  return (
                    <button
                      key={day.date}
                      type="button"
                      onClick={() => setActiveDateIndex(index)}
                      className={`flex flex-col items-center justify-center min-w-[56px] sm:min-w-[60px] h-14 rounded-xl border transition-all duration-200 snap-center relative overflow-hidden
                        ${
                          isActive
                            ? "bg-orange-500 border-orange-500 text-white shadow-lg shadow-orange-500/25 scale-105 z-10"
                            : hasEntries
                              ? "bg-orange-50 border-orange-200 text-orange-600 hover:border-orange-400"
                              : "bg-slate-50 dark:bg-[#112240] border-slate-200/80 dark:border-slate-800/50 text-slate-700 dark:text-slate-300 hover:border-orange-300"
                        }`}
                    >
                      {isActive && (
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
                      )}

                      <span className="text-sm font-bold tracking-tight relative z-10">
                        {parseInt(day.dayNum, 10) || day.dayNum}
                      </span>

                      <span
                        className={`text-[9px] font-semibold uppercase mt-0.5 tracking-wider relative z-10
                              ${
                                isActive
                                  ? "text-orange-100"
                                  : hasEntries
                                    ? "text-orange-400"
                                    : "text-slate-400"
                              }`}
                      >
                        {day.dayName}
                      </span>

                      {hasEntries && !isActive && (
                        <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-emerald-500 rounded-full border border-white shadow-sm" />
                      )}

                      {isActive && (
                        <span className="absolute bottom-0 left-0 w-full h-1 bg-white/40" />
                      )}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => handleScroll("right")}
                className="p-2 rounded-full bg-[#0a192f] hover:bg-orange-500 text-white transition-colors flex items-center justify-center min-w-[34px] h-[34px]"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          {selectedCustomer.value ? (
            <div className="bg-white dark:bg-[#0b1626] border border-slate-200 dark:border-slate-800/80 rounded-xl shadow-sm p-4 space-y-4">
              <div className="flex flex-row flex-wrap items-center gap-3 w-full">
                <button
                  type="button"
                  onClick={copyYesterday}
                  className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs px-5 py-2.5 rounded-xl transition duration-150 shadow-sm hover:shadow-md active:scale-[0.98]"
                >
                  <Copy className="w-4 h-4" />
                  <span>{t("copyYesterday")}</span>
                </button>

                <button
                  type="button"
                  onClick={copyFrequentItems}
                  className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs px-5 py-2.5 rounded-xl transition duration-150 shadow-sm hover:shadow-md active:scale-[0.98]"
                >
                  <Copy className="w-4 h-4" />
                  <span>{t("copyFrequentItems")}</span>
                </button>
              </div>
              <div className="flex items-center justify-center gap-1.5 w-full py-2 text-xs font-semibold text-orange-600 bg-[#FFFBF7] dark:bg-orange-950/20 rounded-xl border border-orange-100/70 dark:border-orange-900/30 shadow-sm">
                <Calendar className="w-3.5 h-3.5 stroke-[2.5]" />
                <span>{t("quantitiesShownBelowAreFor")}</span>
                <span className="font-bold ml-0.5">
                  {currentDayData?.date
                    ? new Date(currentDayData.date).toLocaleDateString(
                        "en-GB",
                        {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        },
                      )
                    : "Fri, 1 May 2026"}
                </span>
              </div>
              <div className="relative flex items-center">
                <Search className="absolute left-3.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder={t("searchProductByName")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-white dark:bg-[#112240] border border-slate-200 dark:border-slate-700/80 rounded-xl text-xs font-medium focus:outline-none focus:ring-1 focus:ring-orange-400 transition"
                />
                <ChevronDown className="absolute right-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between bg-white dark:bg-[#0c1a30]/60 p-2 rounded-xl border border-slate-100 dark:border-slate-800/80 shadow-sm">
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400 pl-2">
                    {t("productDisplayMode")}
                  </span>
                  <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg gap-1">
                    <button
                      type="button"
                      onClick={() => setViewMode("list")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                        viewMode === "list"
                          ? "bg-white dark:bg-[#112240] text-orange-500 shadow-sm"
                          : "text-slate-500 dark:text-slate-400 hover:text-slate-700"
                      }`}
                    >
                      <List className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{t("listView")}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode("grid")}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                        viewMode === "grid"
                          ? "bg-white dark:bg-[#112240] text-orange-500 shadow-sm"
                          : "text-slate-500 dark:text-slate-400 hover:text-slate-700"
                      }`}
                    >
                      <LayoutGrid className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{t("gridView")}</span>
                    </button>
                  </div>
                </div>

                {viewMode === "list" ? (
                  <div className="overflow-x-auto max-h-[460px] overflow-y-auto rounded-xl border border-slate-100 dark:border-slate-800/60 shadow-sm">
                    <table className="w-full text-left border-collapse">
                      <thead className="sticky top-0 z-10 bg-brand-orange dark:bg-slate-900 text-white text-[11px] font-semibold uppercase tracking-wider text-center">
                        <tr>
                          <th className="py-3 px-2 w-10">#</th>
                          <th className="py-3 px-4 text-left">
                            {t("productName")}
                          </th>
                          <th className="py-3 px-3 text-center w-24">
                            {t("wholesale")} (₹)
                          </th>
                          <th className="py-3 px-3 text-center w-24">
                            {t("selling")} (₹)
                          </th>
                          <th className="py-3 px-4 w-32">Qty</th>
                          <th className="py-3 px-3 w-28 text-right pr-6">
                            {t("amount")} (₹)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50 text-xs font-medium text-slate-700 dark:text-slate-300">
                        {filteredProducts?.map((product, index) => (
                          <tr
                            key={product.id}
                            className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20"
                          >
                            <td className="py-3 px-2 text-center text-slate-400">
                              {index + 1}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <span className="text-lg bg-slate-50 dark:bg-slate-800 p-1.5 rounded-lg border border-slate-100 dark:border-slate-700">
                                  📦
                                </span>
                                <p className="font-bold text-slate-800 dark:text-slate-100">
                                  {product.name}
                                </p>
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              <input
                                type="text"
                                inputMode="numeric"
                                maxLength={4}
                                value={product.cost_price}
                                onChange={(e) =>
                                  handlePriceChange(
                                    product.id,
                                    "cost_price",
                                    e.target.value,
                                  )
                                }
                                disabled
                                className="w-full text-center py-1.5 border border-slate-200/80 dark:border-slate-700 rounded-lg bg-slate-50/50 text-slate-600 font-mono text-xs"
                              />
                            </td>
                            <td className="py-3 px-3">
                              <input
                                type="text"
                                inputMode="numeric"
                                maxLength={4}
                                value={product.selling}
                                onChange={(e) =>
                                  handlePriceChange(
                                    product.id,
                                    "selling",
                                    e.target.value,
                                  )
                                }
                                className="w-full text-center py-1.5 border border-slate-200/80 dark:border-slate-700 rounded-lg bg-slate-50/50 text-slate-700 font-mono text-xs font-bold"
                              />
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-between border border-slate-200 dark:border-slate-700 rounded-full bg-white px-2 py-1 max-w-[110px] mx-auto shadow-sm">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleQtyChange(product.id, -1)
                                  }
                                  className="text-orange-500 hover:bg-orange-50 p-1 rounded-full transition"
                                >
                                  <Minus className="w-3.5 h-3.5 stroke-[3]" />
                                </button>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={4}
                                  value={product.qty}
                                  onFocus={(e) => e.target.select()}
                                  onChange={(e) =>
                                    handleManualQtyChange(
                                      product.id,
                                      e.target.value,
                                    )
                                  }
                                  className="w-10 text-center bg-transparent outline-none border-none text-sm font-bold text-slate-800 focus:ring-0"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleQtyChange(product.id, 1)}
                                  className="text-orange-500 hover:bg-orange-50 p-1 rounded-full transition"
                                >
                                  <Plus className="w-3.5 h-3.5 stroke-[3]" />
                                </button>
                              </div>
                            </td>
                            <td className="py-3 px-3 text-right pr-6 font-mono font-bold text-slate-800 dark:text-slate-100">
                              {(product.qty * product.selling).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto pr-1 pb-2">
                    {filteredProducts?.map((product, index) => (
                      <div
                        key={product.id}
                        className="bg-white dark:bg-[#0c1a30]/40 border border-slate-200/80 dark:border-slate-800/80 rounded-2xl p-4 shadow-sm hover:shadow-md transition flex flex-col justify-between space-y-3 relative overflow-hidden"
                      >
                        <span className="absolute top-0 left-0 bg-slate-100 dark:bg-slate-800 text-slate-400 text-[10px] font-bold px-2 py-0.5 rounded-br-lg">
                          #{index + 1}
                        </span>

                        <div className="flex items-start gap-3 pt-1">
                          <span className="text-xl bg-orange-50 dark:bg-orange-950/20 p-2 rounded-xl border border-orange-100/40">
                            📦
                          </span>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-slate-800 dark:text-slate-100 text-xs sm:text-sm truncate">
                              {product.name}
                            </h4>
                            <p className="text-[10px] text-slate-400 font-medium">
                              {t("standardUnitPack")}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 bg-slate-50 dark:bg-slate-900/40 p-2 rounded-xl border border-slate-100 dark:border-slate-800/60">
                          <div>
                            <span className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                              {t("wholesale")} (₹)
                            </span>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={4}
                              value={product.cost_price}
                              onChange={(e) =>
                                handlePriceChange(
                                  product.id,
                                  "cost_price",
                                  e.target.value,
                                )
                              }
                              disabled
                              className="w-full text-center py-1 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-600 font-mono text-xs"
                            />
                          </div>
                          <div>
                            <span className="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                              {t("selling")} (₹)
                            </span>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={4}
                              value={product.selling}
                              onChange={(e) =>
                                handlePriceChange(
                                  product.id,
                                  "selling",
                                  e.target.value,
                                )
                              }
                              className="w-full text-center py-1 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-mono text-xs font-bold"
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-1 border-t border-slate-100 dark:border-slate-800/60">
                          <div className="flex items-center justify-between border border-slate-200 dark:border-slate-700 rounded-full bg-white dark:bg-slate-800 px-2 py-1 w-28 shadow-sm">
                            <button
                              type="button"
                              onClick={() => handleQtyChange(product.id, -1)}
                              className="text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950/40 p-1 rounded-full transition"
                            >
                              <Minus className="w-3 h-3 stroke-[3]" />
                            </button>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={4}
                              value={product.qty}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) =>
                                handleManualQtyChange(
                                  product.id,
                                  e.target.value,
                                )
                              }
                              className="w-8 text-center bg-transparent outline-none border-none text-xs font-black text-slate-800 dark:text-slate-200 focus:ring-0"
                            />
                            <button
                              type="button"
                              onClick={() => handleQtyChange(product.id, 1)}
                              className="text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950/40 p-1 rounded-full transition"
                            >
                              <Plus className="w-3 h-3 stroke-[3]" />
                            </button>
                          </div>

                          <div className="text-right">
                            <span className="block text-[9px] font-bold uppercase tracking-wider text-slate-400">
                              {t("totalAmt")}
                            </span>
                            <span className="font-mono font-black text-slate-800 dark:text-slate-100 text-sm">
                              {formatCurrencyINR(
                                (product.qty * product.selling).toFixed(2),
                              )}{" "}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-stretch">
                  {/* CARD 1: TOTAL PRODUCTS */}
                  <div className="bg-[#F8FAFC] dark:bg-[#112240] p-3 rounded-xl border border-slate-100 dark:border-slate-800/60 flex items-center gap-3">
                    <div className="p-2 bg-[#FFFAF5] dark:bg-orange-950/40 rounded-lg text-orange-500 shrink-0">
                      <Layers className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">
                        {t("totalProducts")}
                      </p>
                      <p className="text-xl font-black text-slate-800 dark:text-white mt-0.5">
                        {computedTotals.uniqueCount}
                      </p>
                    </div>
                  </div>

                  {/* CARD 2: TOTAL QUANTITY */}
                  <div className="bg-[#F8FAFC] dark:bg-[#112240] p-3 rounded-xl border border-slate-100 dark:border-slate-800/60 flex flex-col justify-center">
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">
                      {t("totalQuantity")}
                    </p>
                    <p className="text-xl font-black text-slate-800 dark:text-white mt-0.5">
                      {computedTotals.sumQty}
                    </p>
                  </div>

                  {/* CARD 3: TOTAL AMOUNT */}
                  <div className="bg-[#F8FAFC] dark:bg-[#112240] p-3 rounded-xl border border-slate-100 dark:border-slate-800/60 flex flex-col justify-center">
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">
                      {t("totalAmount")}
                    </p>
                    <p className="text-xl font-black text-orange-600 mt-0.5">
                      {formatCurrencyINR(computedTotals.sumAmount.toFixed(2))}
                    </p>
                  </div>

                  {/* CARD 4: DYNAMIC PAYMENT HANDLING FIELD */}
                  <div className="col-span-1 flex items-center justify-center">
                    {isFullyPaid ? (
                      <div className="w-full h-full min-h-[64px] flex items-center justify-center rounded-xl bg-[#E6FBF3] dark:bg-emerald-950/20 border border-[#BFF3E0] dark:border-emerald-800/50 text-[#00A86B] font-bold text-sm text-center px-4">
                        Fully Paid ✅
                      </div>
                    ) : (
                      <div className="w-full h-full bg-[#F8FAFC] dark:bg-[#112240] p-2 rounded-xl border border-slate-100 dark:border-slate-800/60 flex flex-col sm:flex-row gap-1.5 items-stretch justify-between">
                        {/* Input Entry Box */}
                        <input
                          type="number"
                          min="0"
                          inputMode="numeric"
                          placeholder="Enter payment"
                          value={currentDayData.summary?.new_paid_input || ""}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            markDirty((prev) =>
                              prev.map((d, idx) => {
                                if (idx !== activeDateIndex) return d;
                                const enteredAmount = Number(inputValue || 0);
                                const total = d.products.reduce(
                                  (acc, p) => acc + p.qty * p.selling,
                                  0,
                                );
                                const oldPaid = Number(d.summary?.paid || 0);
                                const oldInput = Number(
                                  d.summary?.new_paid_input || 0,
                                );
                                const actualPaid = oldPaid - oldInput;
                                const pendingBeforeEntry = total - actualPaid;

                                if (enteredAmount > pendingBeforeEntry) {
                                  notify.error(
                                    `Only ₹${pendingBeforeEntry} pending remaining`,
                                  );
                                  return d;
                                }
                                return {
                                  ...d,
                                  summary: {
                                    ...d.summary,
                                    paid: actualPaid + enteredAmount,
                                    new_paid_input: inputValue,
                                  },
                                };
                              }),
                            );
                          }}
                          className="flex-1 min-w-0 px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-semibold outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                        />
                        {/* Internal Inline Mini Stats */}
                        <div className="bg-emerald-50/60 dark:bg-emerald-950/10 border border-emerald-100 dark:border-emerald-900/30 rounded-lg px-2 py-1 flex flex-row sm:flex-col justify-between sm:justify-center items-center shrink-0">
                          <span className="text-[9px] font-bold text-slate-400 uppercase sm:tracking-normal">
                            Paid
                          </span>
                          <span className="text-xs font-black text-emerald-600">
                            {formatCurrencyINR(
                              currentDayData.summary?.paid || 0,
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* CARD 5: PENDING AMOUNT */}
                  <div className="bg-[#F8FAFC] dark:bg-[#112240] p-3 rounded-xl border border-slate-100 dark:border-slate-800/60 flex flex-col justify-center">
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">
                      {t("pending")}
                    </p>
                    <p className="text-xl font-black text-red-500 mt-0.5">
                      {formatCurrencyINR(computedTotals.pending.toFixed(2))}
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-center text-[11px] font-medium text-emerald-500 flex items-center justify-center gap-1.5 pt-1">
                <span className="w-2 h- 2 rounded-full bg-emerald-500 inline-block animate-pulse"></span>
                {t("dataWillBeAutoSaved")}
              </p>
            </div>
          ) : (
            <div className="bg-white dark:bg-[#0b1626] border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl shadow-sm py-16 px-8 flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center mb-5">
                <span className="text-4xl">🛒</span>
              </div>

              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                Please Select a Customer
              </h3>

              <p className="mt-3 max-w-md text-sm text-slate-500 dark:text-slate-400 leading-6">
                Select a customer from the dropdown above to view their
                date-wise products, purchase history, quantities, and payment
                details.
              </p>

              <div className="mt-6 px-5 py-2 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-600 text-sm font-semibold border border-orange-200 dark:border-orange-800">
                👆 Choose a customer to get started
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white dark:bg-[#0c1a30]/50 border border-slate-200 dark:border-slate-800/80 p-5 rounded-xl space-y-4 shadow-sm">
            {selectedCustomer && (
              <>
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-orange-500">
                  {t("customerSummary")}
                </h3>
                <div className="grid grid-cols-2 gap-2 text-xs border-b pb-3">
                  <div>
                    <p className="text-slate-500 text-[10px]">{t("name")}</p>
                    <p className="font-bold text-slate-900 dark:text-white">
                      {selectedCustomer ? selectedCustomer?.label : ""}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-[10px]">{t("mobile")}</p>
                    <p className="font-bold text-slate-900 dark:text-white">
                      {selectedCustomer ? selectedCustomer?.mobile : ""}
                    </p>
                  </div>
                </div>
              </>
            )}
            <div className="flex justify-between">
              <span className="text-slate-500">{t("totalAmount")}</span>
              <span className="font-semibold text-slate-800 dark:text-white">
                {formatCurrencyINR(customerSummary.totalAmount.toFixed(2))}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-slate-500">{t("totalPaid")}</span>
              <span className="font-bold text-emerald-600">
                {formatCurrencyINR(customerSummary.totalPaid.toFixed(2))}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-slate-500">{t("pendingAmount")}</span>
              <span className="font-bold text-rose-600">
                {formatCurrencyINR(customerSummary.pending.toFixed(2))}
              </span>
            </div>

            <div className="flex justify-between border-t pt-2 font-bold">
              <span className="text-slate-500">{t("totalProfit")}</span>
              <span className="text-orange-500">
                {formatCurrencyINR(customerSummary.profit.toFixed(2))}
              </span>
            </div>
          </div>

          {selectedCustomer?.value && (
            <div className="bg-white dark:bg-[#0c1a30]/50 border border-slate-200 dark:border-slate-800/80 p-5 rounded-xl space-y-3 shadow-sm">
              {selectedCustomer?.value && (
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-orange-500">
                  {t("quickActions")}
                </h3>
              )}
              <div className="flex flex-col gap-2 text-xs font-semibold">
                {selectedCustomer?.value && (
                  <button
                    onClick={handleDownloadCSV}
                    className="flex items-center justify-center gap-2 border border-amber-500/30 bg-amber-500/5 text-amber-600 hover:bg-amber-500/10 py-2 rounded-lg transition"
                  >
                    <Download className="w-3.5 h-3.5" /> {t("downloadCSV")}
                  </button>
                )}

                <button
                  onClick={downloadPDF}
                  className="hidden flex items-center justify-center gap-2 border border-rose-500/30 bg-rose-500/5 text-rose-600 hover:bg-rose-500/10 py-2 rounded-lg transition"
                >
                  <FileText className="w-3.5 h-3.5" /> {t("downloadPDF")}
                </button>
                <button
                  onClick={sendWhatsApp}
                  className="hidden flex items-center justify-center gap-2 border border-emerald-500/30 bg-emerald-500/5 text-emerald-600 hover:bg-emerald-500/10 py-2 rounded-lg transition"
                >
                  <MessageSquare className="w-3.5 h-3.5" />{" "}
                  {t("whatsappStatement")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChartProduct;
